

import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Chip, LinearProgress, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import {
  CheckCircle, Cancel, CloudUpload, Delete,
  Description, RocketLaunch, ThumbDown, LinkOff,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { COLORS, TYPE, RADIUS, CONTROL_HEIGHT, ELLIPSIS, FONT_DISPLAY } from "../../../styles/recruiterTheme";
import PublicPageShell, {
  PublicCard, PublicStatus, readJson, networkMessage, validateFile, MAX_UPLOAD_MB,
} from "../PublicPageShell";

const OFFER_BASE = process.env.REACT_APP_API_BASE_URL; 

const ACCEPTED_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".doc", ".docx"];

// ── Document list that candidates must upload ─────────────────────────────────
// Reuses the same categories from OnboardingPage.jsx
const REQUIRED_DOCS = [
  { name: "Aadhar Card",                    category: "Identity",     required: true  },
  { name: "PAN Card",                       category: "Identity",     required: true  },
  { name: "10th Marksheet",                 category: "Education",    required: true  },
  { name: "12th Marksheet",                 category: "Education",    required: true  },
  { name: "Graduation Certificate",         category: "Education",    required: false },
  { name: "Previous Offer Letter",          category: "Professional", required: false },
  { name: "Relieving Letter",               category: "Professional", required: false },
  { name: "Last 3 Months Payslips",         category: "Professional", required: false },
  { name: "Photograph",                     category: "Other",        required: true  },
  { name: "Signed Offer Letter",            category: "Other",        required: true  },
];

const overline = { fontSize: TYPE.overline, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: COLORS.muted, mb: 1 };

// ── Single document upload row ─────────────────────────────────────────────────
function DocRow({ doc, idx, file, disabled, onFileChange, onFileRemove }) {
  const inputRef = useRef(null);
  return (
    <Box
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.5, borderRadius: `${RADIUS.sm}px`,
        border: `1px solid ${file ? COLORS.success : COLORS.border}`,
        bgcolor: file ? COLORS.successSoft : COLORS.surface,
        transition: "background-color 0.15s, border-color 0.15s",
      }}
    >
      {/* Status icon */}
      <Box sx={{ flexShrink: 0, display: "flex" }}>
        {file
          ? <CheckCircle sx={{ fontSize: 20, color: COLORS.success }} />
          : <Box sx={{
              width: 20, height: 20, borderRadius: "50%",
              border: `2px solid ${doc.required ? COLORS.error : COLORS.borderStrong}`,
              bgcolor: COLORS.surface,
            }} />
        }
      </Box>

      {/* Name + category */}
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: COLORS.ink }}>
          {doc.name}
          {doc.required && (
            <Typography component="span" sx={{ fontSize: TYPE.caption, color: COLORS.error, ml: 0.5 }}>*</Typography>
          )}
        </Typography>
        <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>{doc.category}</Typography>
      </Box>

      {/* File info or upload button */}
      {file ? (
        <Box display="flex" alignItems="center" gap={0.5} minWidth={0} sx={{ maxWidth: { xs: 130, sm: 220 } }}>
          <Tooltip title={file.name}>
            <Chip
              icon={<Description sx={{ fontSize: 14 }} />}
              label={file.name}
              size="small"
              sx={{ bgcolor: COLORS.surface, color: COLORS.primaryDark, border: `1px solid ${COLORS.border}`, minWidth: 0,
                "& .MuiChip-label": ELLIPSIS }}
            />
          </Tooltip>
          <Tooltip title="Remove file">
            <span>
              <IconButton size="small" aria-label={`Remove ${doc.name}`} disabled={disabled}
                onClick={() => onFileRemove(idx)} sx={{ color: COLORS.error }}>
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : (
        <Button
          size="small" variant="outlined"
          startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          sx={{ height: CONTROL_HEIGHT.sm, flexShrink: 0, textTransform: "none", fontWeight: 600,
            borderColor: COLORS.borderStrong }}
        >
          Upload
        </Button>
      )}

      <input
        type="file" hidden ref={inputRef}
        accept={ACCEPTED_EXTS.join(",")}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) onFileChange(idx, f);
          e.target.value = "";
        }}
      />
    </Box>
  );
}

// ── Main portal component ─────────────────────────────────────────────────────
export default function CandidateOfferPortal() {
  const { token } = useParams();

  const [phase,     setPhase]     = useState("loading"); // loading | offer | done | declined | error
  const [offerData, setOfferData] = useState(null);
  const [error,     setError]     = useState("");
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [files,     setFiles]     = useState({});       // { docIndex: File }
  const [submitting, setSubmitting] = useState(null);   // "accept" | "decline" | null
  const [justAccepted, setJustAccepted] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const inFlight = useRef(false);

  // ── Load offer data ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (!token) { setPhase("error"); setLoadError("Invalid link."); return; }
    setPhase("loading"); setLoadError("");

    fetch(`${OFFER_BASE}/onboarding/offer-portal/${token}`)
      .then(readJson)
      .then(data => {
        if (cancelled) return;
        if (!data.success) {
          setLoadError(data.message || "This offer link is invalid or has expired.");
          setPhase("error");
          return;
        }
        if (data.already_accepted) {
          setPhase("done");
          return;
        }
        setOfferData(data.candidate || {});
        setPhase("offer");
      })
      .catch(e => {
        if (cancelled) return;
        setLoadError(networkMessage(e));
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  // ── File handlers ────────────────────────────────────────────────────────────
  const handleFileChange = (idx, file) => {
    const msg = validateFile(file, ACCEPTED_EXTS);
    if (msg) { setError(`${REQUIRED_DOCS[idx].name}: ${msg}`); return; }
    setError("");
    setFiles(p => ({ ...p, [idx]: file }));
  };
  const handleFileRemove = (idx) => setFiles(p => { const n = { ...p }; delete n[idx]; return n; });

  // ── Validate required docs before submit ─────────────────────────────────────
  const missingRequired = REQUIRED_DOCS
    .map((doc, idx) => ({ ...doc, idx }))
    .filter(doc => doc.required && !files[doc.idx]);

  // ── Submit (accept + upload) ──────────────────────────────────────────────────
  const handleAccept = async () => {
    if (inFlight.current) return;
    if (missingRequired.length > 0) {
      setError(`Please upload all required documents: ${missingRequired.map(d => d.name).join(", ")}`);
      return;
    }
    inFlight.current = true;
    setSubmitting("accept"); setError("");

    const fd = new FormData();
    fd.append("action", "accept");

    REQUIRED_DOCS.forEach((doc, idx) => {
      fd.append(`doc_name_${idx}`,     doc.name);
      fd.append(`doc_category_${idx}`, doc.category);
      if (files[idx]) fd.append(`file_${idx}`, files[idx]);
    });

    try {
      const res  = await fetch(`${OFFER_BASE}/onboarding/offer-portal/${token}/respond`, {
        method: "POST",
        body: fd,
        // NOTE: no Content-Type header — browser sets it with boundary for multipart
      });
      const data = await readJson(res);
      if (!data.success) {
        setError(data.message || "Submission failed. Please try again.");
        return;
      }
      setJustAccepted(true);
      setPhase("done");
    } catch (e) {
      setError(networkMessage(e));
    } finally {
      setSubmitting(null);
      inFlight.current = false;
    }
  };

  // ── Decline ───────────────────────────────────────────────────────────────────
  const handleDecline = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting("decline"); setError("");

    try {
      const fd = new FormData();
      fd.append("action", "decline");
      const res  = await fetch(`${OFFER_BASE}/onboarding/offer-portal/${token}/respond`, {
        method: "POST", body: fd,
      });
      const data = await readJson(res);
      if (data.success) { setConfirmDecline(false); setPhase("declined"); }
      else { setConfirmDecline(false); setError(data.message || "Could not process your response."); }
    } catch (e) {
      setConfirmDecline(false);
      setError(networkMessage(e));
    } finally {
      setSubmitting(null);
      inFlight.current = false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  RENDER PHASES
  // ─────────────────────────────────────────────────────────────────────────────
  let body;

  if (phase === "loading") {
    body = <PublicStatus loading message="Loading your offer…" />;
  } else if (phase === "error") {
    const network = /reach the server|HTTP 5/.test(loadError);
    body = (
      <PublicStatus tone="error" icon={<LinkOff />} title={network ? "Could not load your offer" : "Link unavailable"}
        message={network ? loadError : `${loadError || "This offer link is invalid or has expired."} Please contact your HR representative.`}
        action={network ? { label: "Try again", onClick: () => setReloadKey(k => k + 1) } : null} />
    );
  } else if (phase === "done") {
    body = (
      <PublicStatus tone="success" icon={<CheckCircle />}
        title={justAccepted ? "Offer accepted!" : "Offer already accepted"}
        message={justAccepted
          ? "Thank you for accepting the offer. Your onboarding has been initiated. Our HR team will be in touch with the next steps shortly."
          : "You have already accepted this offer. Our HR team will be in touch with the next steps."} />
    );
  } else if (phase === "declined") {
    body = (
      <PublicStatus tone="muted" icon={<ThumbDown />} title="Offer declined"
        message="We have recorded your response. We appreciate your time and wish you all the best." />
    );
  } else {
    // ── MAIN OFFER VIEW ───────────────────────────────────────────────────────
    const uploadedCount = Object.keys(files).length;
    const totalDocs     = REQUIRED_DOCS.length;
    const uploadPct     = totalDocs ? Math.round((uploadedCount / totalDocs) * 100) : 0;
    const busy          = !!submitting;
    const renderRows = (required) => REQUIRED_DOCS
      .map((doc, idx) => ({ doc, idx }))
      .filter(({ doc }) => doc.required === required)
      .map(({ doc, idx }) => (
        <DocRow key={idx} doc={doc} idx={idx}
          file={files[idx] || null}
          disabled={busy}
          onFileChange={handleFileChange}
          onFileRemove={handleFileRemove}
        />
      ));

    body = (
      <>
        {/* ── Header card ───────────────────────────────────────────────── */}
        <PublicCard sx={{ p: 0, overflow: "hidden" }}>
          <Box sx={{ background: `linear-gradient(135deg, ${COLORS.primaryDeep} 0%, ${COLORS.primaryDark} 100%)`, px: { xs: 2, sm: 3 }, py: 3 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: TYPE.overline, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
              Offer letter
            </Typography>
            <Typography component="h1" sx={{ color: "#fff", fontFamily: FONT_DISPLAY, fontWeight: 700,
              fontSize: { xs: TYPE.section, sm: TYPE.page }, lineHeight: 1.25, wordBreak: "break-word" }}>
              Congratulations{offerData?.candidatename ? `, ${offerData.candidatename}` : ""}!
            </Typography>
            {(offerData?.jobRole || offerData?.companyName) && (
              <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: TYPE.body, mt: 0.5 }}>
                {[offerData?.jobRole, offerData?.companyName].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Box>

          {/* Offer letter link if available */}
          {offerData?.offer_letter_url && (
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: `1px solid ${COLORS.border}`,
              display: "flex", alignItems: "center", gap: 1 }}>
              <Description sx={{ fontSize: 20, color: COLORS.primary }} />
              <Typography sx={{ fontSize: TYPE.body, color: COLORS.ink, flex: 1 }}>Offer letter document</Typography>
              <Button
                size="small" variant="outlined"
                href={offerData.offer_letter_url} target="_blank" rel="noopener noreferrer"
                sx={{ height: CONTROL_HEIGHT.sm, textTransform: "none", fontWeight: 600 }}
              >
                View PDF
              </Button>
            </Box>
          )}

          <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
            <Typography sx={{ fontSize: TYPE.body, color: COLORS.ink, lineHeight: 1.7 }}>
              Please review the offer above and upload the required documents below.
              Once you click <strong>Accept offer</strong>, your onboarding
              will begin and our HR team will reach out with the next steps.
            </Typography>
          </Box>
        </PublicCard>

        {/* ── Document upload card ──────────────────────────────────────── */}
        <PublicCard>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.ink }}>
              Document upload
            </Typography>
            <Typography sx={{ fontSize: TYPE.caption, fontWeight: 600, fontVariantNumeric: "tabular-nums",
              color: uploadedCount === totalDocs ? COLORS.success : COLORS.muted }}>
              {uploadedCount} / {totalDocs} uploaded
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate" value={uploadPct}
            sx={{ height: 6, borderRadius: 3, mb: 1, bgcolor: COLORS.border,
              "& .MuiLinearProgress-bar": { bgcolor: uploadPct === 100 ? COLORS.success : COLORS.primary, borderRadius: 3 } }}
          />
          <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, mb: 2 }}>
            Accepted files: PDF, PNG, JPG, DOC, DOCX · Max {MAX_UPLOAD_MB} MB per document
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography sx={overline}>
            Required <Typography component="span" sx={{ color: COLORS.error, fontSize: "inherit" }}>*</Typography>
          </Typography>
          <Box display="flex" flexDirection="column" gap={1} mb={2.5}>
            {renderRows(true)}
          </Box>

          <Typography sx={overline}>Optional</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {renderRows(false)}
          </Box>
        </PublicCard>

        {/* ── Action buttons ────────────────────────────────────────────── */}
        <PublicCard>
          {missingRequired.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Still required: {missingRequired.map(d => d.name).join(", ")}
            </Alert>
          )}
          {submitting === "accept" && (
            <Box mb={2}>
              <LinearProgress sx={{ borderRadius: 1 }} />
              <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted, mt: 0.5 }}>
                Uploading documents — please keep this page open…
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
            <Button
              variant="outlined" color="error"
              startIcon={<Cancel />}
              disabled={busy}
              onClick={() => setConfirmDecline(true)}
              sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              Decline offer
            </Button>
            <Button
              variant="contained"
              startIcon={submitting === "accept"
                ? <CircularProgress size={16} color="inherit" />
                : <RocketLaunch />}
              disabled={busy || missingRequired.length > 0}
              onClick={handleAccept}
              sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600, px: 3, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              {submitting === "accept" ? "Submitting…" : "Accept offer & submit documents"}
            </Button>
          </Box>
        </PublicCard>

        <Dialog open={confirmDecline} onClose={() => !busy && setConfirmDecline(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Decline this offer?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: TYPE.body }}>
              Your response will be sent to our HR team and this link will stop working. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDecline(false)} disabled={busy} sx={{ textTransform: "none", fontWeight: 600 }}>
              Keep offer
            </Button>
            <Button variant="contained" color="error" onClick={handleDecline} disabled={busy}
              startIcon={submitting === "decline" ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ height: CONTROL_HEIGHT.md, textTransform: "none", fontWeight: 600 }}>
              {submitting === "decline" ? "Declining…" : "Decline offer"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return <PublicPageShell subtitle="Offer & onboarding">{body}</PublicPageShell>;
}
